import React, { useEffect, useState, useRef } from "react";
import Icon9 from "../img/447e447e10282956b81289a96893ff9dcbf5c994c06cab37c72bc161cd26f0aa.svg";
import Icon19 from "../img/b4842688541f4c6389ef36bfe42945d814ca91f46ea5b94e336d43be5e0cabcf.svg";
import { useMsal } from "@azure/msal-react";
import { Client } from "@microsoft/microsoft-graph-client";

/**
 * Each key is a separate document library (its own drive).
 * Replace with the *exact* driveId for each library name you want to manage.
 */
const FOLDER_INFO = {
  Billings: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKXynwmuoEb_Qq_tPUkIepnW",
  },
  "Change Orders": {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKVFNMt8TsItRrB2IB8RvFzW",
  },
  "Close-Out Documents": {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKWc8VWb4BWAR6ohBv4ox_pt",
  },
  Photos: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKUPLLiaC2DeR7VbZbI_AFqV",
  },
  "POs & Cutlists": {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKXjepiTUmQFSoRX_3Vleh-E",
  },
  Reports: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKUQhhvuKkx_QYb-XE3DIbTA",
  },
  Safety: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKVMnDsvCT3DTLGNbmpaQu_x",
  },
  Submittals: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKUGFA2jWewTS7klVu6MeAaj",
  },
  "Certified Payroll": {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKUTKvmK5SRaTrORAe19dxOg",
  },
  "Insurance & Bonds": {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKXuiMvMdFvuR5IZBSZmVE4a",
  },
  // Master: {
  //   driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKX3WjpJRec5SLKkDOLSmGuk",
  // },
  // "Project Folders": {
  //   driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKVT0gD-31nnSb8kDg_mDFIC",
  // },
  Compliance: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKUV8BOuqoSQQJw3J8dZP1_u",
  },
  Documents: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKWd85EtwcBLQbw6TD75O_FT",
  },
  Subcontracts: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKWDkJIzdZLFS7kJYGVSS8Ea",
  },
  Contracts: {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKVlBwV8Mr_nQJKvl_rlJHqj",
  },
  "Purchase Orders": {
    driveId: "b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKXjepiTUmQFSoRX_3Vleh-E",
  },
};


const DocManagementLayout = ({ projectData }) => {
  const { instance, accounts } = useMsal();
  const [folders, setFolders] = useState([]);
  const prevOpportunityNo = useRef(null);

  useEffect(() => {
    if (accounts.length === 0 || !projectData?.opportunityNo) return;

    if (prevOpportunityNo.current !== projectData.opportunityNo) {
      prevOpportunityNo.current = projectData.opportunityNo;
      initializeFolders();
    }
  }, [accounts, projectData]);

  const initializeFolders = async () => {
    try {
      // Acquire token for Graph
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/Sites.ReadWrite.All"],
        account: accounts[0],
      });

      const client = Client.init({
        authProvider: (done) => done(null, tokenResponse.accessToken),
      });

      const updatedFolders = [];

      // For each library/drive, ensure a subfolder named opportunityNo exists at the root
      for (const [folderName, { driveId }] of Object.entries(FOLDER_INFO)) {
        const subFolder = await ensureFolderExistsInRoot(
          client,
          driveId,
          projectData.opportunityNo
        );
        if (subFolder) {
          updatedFolders.push({
            id: subFolder.id,
            name: folderName,
            sharepointUrl: subFolder.sharepointUrl,
          });
        }
      }

      setFolders(updatedFolders);
    } catch (error) {
      console.error("❌ Error initializing folders:", error);
    }
  };

  /**
   * Fetch the folders in the root of a given drive.
   */
  const fetchRootChildren = async (client, driveId) => {
    try {
      const response = await client.api(`/drives/${driveId}/root/children`).get();
      return response.value
        .filter((item) => item.folder)
        .map((item) => ({
          id: item.id,
          name: item.name,
          sharepointUrl: item.webUrl,
        }));
    } catch (error) {
      console.error("❌ Error fetching root children for drive:", driveId, error);
      return [];
    }
  };

  /**
   * Ensures a subfolder named folderName exists at the *root* of driveId.
   * Returns the existing folder object or a newly created one.
   */
  const ensureFolderExistsInRoot = async (client, driveId, folderName) => {
    const existing = await fetchRootChildren(client, driveId);
    const match = existing.find((item) => item.name === folderName);

    if (match) {
      console.log(`✅ Folder "${folderName}" already exists in drive: ${driveId}`);
      return match;
    }
    return createFolderInRoot(client, driveId, folderName);
  };

  /**
   * Creates a new folder in the root of driveId.
   */
  const createFolderInRoot = async (client, driveId, folderName) => {
    try {
      const response = await client
        .api(`/drives/${driveId}/root/children`)
        .post({
          name: folderName,
          folder: {},
          "@microsoft.graph.conflictBehavior": "rename",
        });

      console.log(`📂 Created Folder: "${folderName}" in Drive: ${driveId}`);
      return {
        id: response.id,
        name: folderName,
        sharepointUrl: response.webUrl,
      };
    } catch (error) {
      console.error(
        `❌ Error creating folder "${folderName}" in drive ${driveId}:`,
        error
      );
      return null;
    }
  };

  return (
    <div className="flex flex-col px-7 pb-7 w-full rounded-none bg-stone-50">
      <div className="flex flex-col self-end mt-4 max-w-full text-base w-[259px]">
        {folders.map(({ id, name, sharepointUrl }, idx) => (
          <NavigationItem
            key={id}
            title={name}
            url={sharepointUrl}
            icon={Icon9}
            indicator={Icon19}
            className={idx > 0 ? "mt-4" : ""}
          />
        ))}
      </div>
    </div>
  );
};

const NavigationItem = ({ title, icon, indicator, url, className }) => (
  <div
    className={`flex gap-4 items-center w-full max-w-[259px] ${className} cursor-pointer`}
    onClick={() => window.open(url, "_blank")}
  >
    <img
      src={indicator}
      className="object-contain shrink-0 self-stretch my-auto aspect-[0.5] w-[5px]"
      alt="Indicator"
    />
    <div className="flex gap-3 items-center self-stretch my-auto">
      <img
        src={icon}
        className="object-contain shrink-0 self-stretch my-auto aspect-[1.31] w-[21px]"
        alt="Icon"
      />
      <div className="self-stretch my-auto w-[206px]" tabIndex="0">
        {title}
      </div>
    </div>
  </div>
);

export default DocManagementLayout;
